import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useLabels, useStatusLabel } from '../config/useLabels';

import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Enrollment, Program, Invoice, getEnrollmentPaymentStatus, getScheduleTimeRange } from '../types/program';
import { getStatusColor } from '../types/status';
import { formatDateDisplay } from '../utils/date';
import { useUrlTabState } from '../hooks/useUrlTabState';
import { BrandBadge } from '../components/BrandBadge';
import { useCartHeldInvoiceIds } from '../hooks/useCartHeldInvoiceIds';

interface Student {
  id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string | null;
  metadata?: unknown;
}

interface EnrollmentWithDetails extends Enrollment {
  students: Student;
  programs: Program;
  invoices: Invoice[];
}

interface StudentEnrollments {
  student: Student;
  enrollments: EnrollmentWithDetails[];
}

type RangeFilter = 'month' | 'year' | 'all';
const RANGE_FILTERS: readonly RangeFilter[] = ['month', 'year', 'all'];

function hasBhcsWaiver(md: unknown): boolean {
  if (!md || typeof md !== 'object') return false;
  return 'bhcs_waiver' in (md as Record<string, unknown>);
}

interface EnrollmentHistoryNavigationState {
  selectedStudentIds?: string[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object';
}

function getSelectedStudentIdsFromNavState(state: unknown): string[] | null {
  if (!isRecord(state)) return null;
  if (!('selectedStudentIds' in state)) return null;
  const ids = (state as EnrollmentHistoryNavigationState).selectedStudentIds;
  if (!Array.isArray(ids)) return null;
  if (!ids.every((id) => typeof id === 'string')) return null;
  return ids;
}

export default function EnrollmentHistory() {
  // Invoices a bank bundle payment is still debiting: no payments row yet, but money is on its way.
  const cartHeldInvoiceIds = useCartHeldInvoiceIds();
  const navigate = useNavigate();
  const t = useLabels();
  const statusLabel = useStatusLabel();
  const location = useLocation();
  const { user, isLoading: authLoading } = useAuth();

  const [isMobile, setIsMobile] = useState(false);
  const [range, setRange] = useUrlTabState<RangeFilter>('range', RANGE_FILTERS, 'year');
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || /Mobi|Android/i.test(navigator.userAgent));
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/dashboard');
  };

  const toggleStudent = (id: string) => {
    setSelectedStudentIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // Compute date range bounds (UTC) for filters
  const { startIso, endIso } = useMemo(() => {
    if (range === 'all') return { startIso: null as string | null, endIso: null as string | null };
    const now = new Date();
    if (range === 'month') {
      const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0));
      const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1, 0, 0, 0));
      return { startIso: start.toISOString(), endIso: end.toISOString() };
    }
    const start = new Date(Date.UTC(now.getUTCFullYear(), 0, 1, 0, 0, 0));
    const end = new Date(Date.UTC(now.getUTCFullYear() + 1, 0, 1, 0, 0, 0));
    return { startIso: start.toISOString(), endIso: end.toISOString() };
  }, [range]);

  const { data: inactiveEnrollments, isLoading, error } = useQuery<EnrollmentWithDetails[], Error>({
    queryKey: ['enrollment-history', user?.email],
    queryFn: async () => {
      if (!user?.email) return [];

      const { data: parentData, error: parentError } = await supabase
        .from('parents')
        .select('id')
        .eq('email', user.email)
        .single();

      if (parentError) throw parentError;
      if (!parentData) return [];

      const { data: studentParents, error: studentParentsError } = await supabase
        .from('student_parents')
        .select('student_id')
        .eq('parent_id', parentData.id);

      if (studentParentsError) throw studentParentsError;
      const studentIds = studentParents?.map(sp => sp.student_id) || [];
      if (studentIds.length === 0) return [];

      const { data, error: enrollmentsError } = await supabase
        .from('enrollments')
        .select(`
          id,
          student_id,
          program_id,
          created_at,
          updated_at,
          is_active,
          students!inner (
            id,
            first_name,
            last_name,
            date_of_birth,
            metadata
          ),
          programs!inner (
            id,
            name,
            type,
            price,
            billing_type,
            brand_key,
            active,
            start_date,
            end_date,
            schedule_cron,
            duration,
            created_at,
            updated_at
          ),
          invoices!inner (
            id,
            enrollment_id,
            total_amount,
            amount_paid,
            status,
            due_date,
            payments (
              status
            )
          )
        `)
        .in('student_id', studentIds)
        .eq('is_active', false)
        .order('created_at', { ascending: false });

      if (enrollmentsError) throw enrollmentsError;
      return (data as unknown as EnrollmentWithDetails[]) || [];
    },
    enabled: !!user?.email && !authLoading
  });

  const studentOptions = useMemo(() => {
    const map = new Map<string, { id: string; first_name: string; last_name: string; active: boolean }>();
    (inactiveEnrollments || []).forEach((e) => {
      // Student "active" column is not selected here; default to true for styling.
      map.set(e.students.id, {
        id: e.students.id,
        first_name: e.students.first_name,
        last_name: e.students.last_name,
        active: true
      });
    });
    const list = Array.from(map.values());
    list.sort((a, b) => a.first_name.localeCompare(b.first_name));
    return list;
  }, [inactiveEnrollments]);

  const navSelectedStudentIds = useMemo(() => {
    return getSelectedStudentIdsFromNavState(location.state);
  }, [location.state]);

  // Initialize selection to "all on" when students load
  useEffect(() => {
    if (studentOptions.length > 0) {
      setSelectedStudentIds((prev) => {
        if (prev.length > 0) return prev;

        if (navSelectedStudentIds && navSelectedStudentIds.length > 0) {
          const available = new Set(studentOptions.map((s) => s.id));
          const filtered = navSelectedStudentIds.filter((id) => available.has(id));
          if (filtered.length > 0) return filtered;
        }

        return studentOptions.map((s) => s.id);
      });
    }
  }, [studentOptions, navSelectedStudentIds]);

  const filteredInactiveEnrollments = useMemo(() => {
    if (studentOptions.length > 0 && selectedStudentIds.length === 0) return [];
    const selected = new Set(selectedStudentIds);
    return (inactiveEnrollments || []).filter((e) => {
      if (selectedStudentIds.length > 0 && !selected.has(e.students.id)) return false;
      if (!startIso || !endIso) return true;
      const created = new Date(e.created_at).toISOString();
      return created >= startIso && created < endIso;
    });
  }, [inactiveEnrollments, selectedStudentIds, startIso, endIso, studentOptions.length]);

  const inactiveStudentEnrollments: StudentEnrollments[] = useMemo(() => {
    const groups = filteredInactiveEnrollments.reduce((acc: StudentEnrollments[], enrollment) => {
      const existingStudent = acc.find(item => item.student.id === enrollment.students.id);
      if (existingStudent) {
        existingStudent.enrollments.push(enrollment);
      } else {
        acc.push({
          student: enrollment.students,
          enrollments: [enrollment]
        });
      }
      return acc;
    }, []);

    // newest enrollments first per-student
    groups.forEach(g => {
      g.enrollments.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    });

    // sort students by latest inactive enrollment date
    groups.sort((a, b) => {
      const aLatest = a.enrollments[0]?.created_at;
      const bLatest = b.enrollments[0]?.created_at;
      return new Date(bLatest || 0).getTime() - new Date(aLatest || 0).getTime();
    });

    return groups;
  }, [filteredInactiveEnrollments]);

  const studentIdToWaiverSigned = useMemo(() => {
    const map = new Map<string, boolean>();
    inactiveStudentEnrollments.forEach(({ student }) => {
      map.set(student.id, hasBhcsWaiver(student.metadata));
    });
    return map;
  }, [inactiveStudentEnrollments]);

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <button onClick={handleGoBack} className="flex items-center text-blue-600 hover:text-blue-800 font-medium mb-6 transition-colors">
          <ChevronLeft className="w-5 h-5 mr-1" />
          Back
        </button>
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">{t('header.enrollmentHistory')}</h1>
        <div className="bg-white p-6 rounded-lg shadow">
          <p className="text-sm sm:text-base text-red-600">
            Error loading enrollment history. {error.message}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${isMobile ? 'p-2 space-y-4' : 'p-4 space-y-6'}`}>
      <button
        onClick={handleGoBack}
        className="flex items-center text-blue-600 hover:text-blue-800 font-medium transition-colors w-fit"
        aria-label="Go back"
      >
        <ChevronLeft className="w-5 h-5 mr-1" />
        Back
      </button>

      <div className="flex items-baseline justify-between gap-3">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">{t('header.enrollmentHistory')}</h1>
      </div>

      {/* Filters (match Payments/Invoice history UI) */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-lg border border-gray-200 bg-white overflow-hidden">
          <button
            type="button"
            className={`px-3 py-1.5 text-sm focus:outline-none ${range === 'month' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'} rounded-l-lg`}
            onClick={() => setRange('month')}
          >
            Month
          </button>
          <button
            type="button"
            className={`px-3 py-1.5 text-sm focus:outline-none ${range === 'year' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
            onClick={() => setRange('year')}
          >
            Year
          </button>
          <button
            type="button"
            className={`px-3 py-1.5 text-sm focus:outline-none ${range === 'all' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'} rounded-r-lg`}
            onClick={() => setRange('all')}
          >
            All
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          {studentOptions.map((s) => {
            const isOn = selectedStudentIds.includes(s.id);
            const classes = isOn ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700';
            return (
              <button
                key={s.id}
                type="button"
                className={`px-3 py-1.5 text-sm rounded-full focus:outline-none transition-colors ${classes}`}
                onClick={() => toggleStudent(s.id)}
              >
                {s.first_name}
              </button>
            );
          })}
        </div>
      </div>

      {inactiveStudentEnrollments.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <p className="text-sm sm:text-base text-gray-700">
            {studentOptions.length > 0 && selectedStudentIds.length === 0
              ? 'Select at least one student to view enrollments.'
              : 'You don’t have any inactive enrollments yet.'}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {inactiveStudentEnrollments.map(({ student, enrollments }) => (
            <div key={student.id} className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
              <div className="p-3 sm:p-4 border-b flex flex-col md:flex-row md:items-center md:justify-between gap-2 bg-gray-50">
                <div className="flex-1">
                  <h3 className="text-base sm:text-lg font-medium text-gray-700">
                    {student.first_name} {student.last_name}
                    {!studentIdToWaiverSigned.get(student.id) && (
                      <Link
                        to="/student-waiver"
                        className="ml-2 inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-[10px] sm:text-xs font-medium bg-yellow-500 text-white shadow-sm hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                        title="Sign required waiver"
                      >
                        Waiver required
                      </Link>
                    )}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-500">
                    Age: {(() => {
                      if (!student.date_of_birth) {
                        return 'Not provided';
                      }

                      const today = new Date();
                      const [y, m, d] = student.date_of_birth.split('-').map((part) => parseInt(part, 10));
                      const birthDate = new Date(y, m - 1, d);

                      let age = today.getFullYear() - birthDate.getFullYear();
                      const monthDiff = today.getMonth() - birthDate.getMonth();
                      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                        age--;
                      }
                      return age;
                    })()}
                  </p>
                </div>
              </div>

              <div className="divide-y divide-gray-100">
                {enrollments.map((enrollment) => {
                  const invoice = enrollment.invoices?.[0];
                  const amountDue = invoice ? invoice.total_amount - invoice.amount_paid : 0;
                  const paymentStatus = getEnrollmentPaymentStatus(enrollment, enrollment.invoices, cartHeldInvoiceIds);
                  const isOutstanding = paymentStatus.toLowerCase() !== 'paid' || amountDue > 0;

                  return (
                    <Link
                      key={enrollment.id}
                      to={`/enrollments/${enrollment.id}`}
                      className="p-3 sm:p-4 flex flex-col gap-2 cursor-pointer transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-gray-400"
                      title="View enrollment details"
                      tabIndex={0}
                      aria-label={`View details for inactive enrollment in ${enrollment.programs.name}`}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      <div className="space-y-2">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-2 flex-1 min-w-0 flex-wrap">
                            <h4 className="text-base sm:text-lg font-medium text-gray-900">
                              {enrollment.programs.name}
                            </h4>
                            <BrandBadge brand={enrollment.programs.brand_key} size="xs" className="mt-1" />
                          </div>

                          {isOutstanding && (
                            <div className="flex-shrink-0">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(paymentStatus)}`}>
                                {statusLabel(paymentStatus)}
                              </span>
                            </div>
                          )}
                        </div>

                        {isOutstanding && (
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-4">
                              <span className="text-xs sm:text-sm text-gray-500">
                                {getScheduleTimeRange(enrollment.programs.schedule_cron)}
                              </span>
                              <span className="text-xs text-gray-500">
                                ({formatDateDisplay(enrollment.programs.start_date)} - {formatDateDisplay(enrollment.programs.end_date)})
                              </span>
                            </div>

                            <div className="text-xs sm:text-sm font-medium text-gray-700">
                              ${amountDue.toFixed(2)}
                            </div>
                          </div>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


